// Login Page
// Email + password authentication with error handling

import React, { useState, useEffect } from 'react';
import { apiUrl } from './utils/api';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: true,
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(null);
  const [brandError, setBrandError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    fetch(apiUrl('/api/company-branding'), { signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error(response.status === 404 ? 'Company address not found. Please check your sign-in address.' : 'Company sign-in is temporarily unavailable. Please try again later.');
        return response.json();
      })
      .then(setBrand)
      .catch(error => { if (error.name !== 'AbortError') setBrandError(error.message); });
    return () => controller.abort();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!brand || brandError) return;
    setError('');
    setLoading(true);

    // Validation
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    // Attempt login
    const result = await login(formData.email, formData.password);

    if (result.success) {
      // Check if user needs to change password
      const storedUser = JSON.parse(localStorage.getItem('doorbase_user'));
      if (storedUser && storedUser.forcePasswordChange) {
        navigate('/change-password');
      } else {
        // Redirect to dashboard
        navigate('/');
      }
    } else {
      // Show error
      setError(result.error || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: brand?.subdomain === 'adg' ? 'linear-gradient(135deg, #112573 0%, #123d8b 60%, #007bc5 100%)' : '#eff6ff' }}>
      <div className="max-w-lg w-full rounded-2xl bg-white p-6 sm:p-10 shadow-2xl" style={{ borderTop: brand?.subdomain === 'adg' ? '6px solid #ff8900' : '6px solid #2563eb' }}>
        {/* Logo and Title */}
        <div className="text-center mb-8">
          {brand?.logoUrl ? <img src={brand.logoUrl} alt={`${brand.companyName} logo`} className="w-full max-w-sm max-h-48 object-contain mx-auto mb-6" /> : <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-lg mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{brand?.logoUrl ? 'Welcome to your company workspace' : (brand?.companyName || 'DoorBase')}</h1>
          <p className="text-gray-600">{brand?.logoUrl ? brand.companyName : 'Sign in to your account'}</p>
        </div>

        {/* Login Card */}
        <div>
          {brandError && <p role="alert" className="mb-6 text-red-700">{brandError}</p>}
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <svg className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">Login failed</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                disabled={loading}
              />
            </div>

            {/* Password Field */}
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
              />
            </div>

            {/* Remember Me */}
            <div className="mb-6 flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
                <span className="ml-2 text-sm text-gray-700">Remember me</span>
              </label>

              {/* Forgot Password Link (future feature) */}
              {!brand?.subdomain && <a href="#" className="text-sm text-blue-600 hover:text-blue-800 transition" onClick={(e) => e.preventDefault()}>
                Forgot password?
              </a>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !brand || Boolean(brandError)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Sign in
                </>
              )}
            </button>
          </form>

          {/* Signup Link */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            {!brand?.subdomain && <p className="text-sm text-gray-600 text-center">
              Don't have an account?{' '}
              <Link to="/signup" className="text-blue-600 hover:text-blue-800 transition font-semibold">
                Start free trial
              </Link>
            </p>}
            <p className="text-xs text-gray-500 text-center mt-2">
              Need help? <a href="mailto:brian@doorbase.co.uk" className="text-blue-600 hover:text-blue-800 transition">
                Contact support
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {brand?.subdomain ? `Powered by DoorBase · ${new Date().getFullYear()}` : `DoorBase © ${new Date().getFullYear()} — Secure Business Management`}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
